import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getParamsFromUrl } from '@shared/lib/url-params';
import { useNotifications } from '@entities/notifications';

import { parseBridgeCallTarget } from '../lib/call-target-parser';
import type { SipTarget } from '../model/types';

interface UseUrlCallTargetParams {
	isRegistered: boolean;
	onStart: (target: SipTarget) => Promise<string | null>;
}

const CALL_TARGET_QUERY_PARAM = 'target';

export const useUrlCallTarget = ({ isRegistered, onStart }: UseUrlCallTargetParams) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { pushNotification } = useNotifications();

	useEffect(() => {
		const removeTargetFromCurrentUrl = () => {
			const searchParams = new URLSearchParams(location.search);
			if (!searchParams.has(CALL_TARGET_QUERY_PARAM)) {
				return;
			}

			searchParams.delete(CALL_TARGET_QUERY_PARAM);

			const nextSearch = searchParams.toString();
			navigate(
				{
					pathname: location.pathname,
					search: nextSearch ? `?${nextSearch}` : '',
					hash: location.hash,
				},
				{ replace: true }
			);
		};

		const { target } = getParamsFromUrl(location.search, [CALL_TARGET_QUERY_PARAM] as const);
		if (target === undefined) return;

		try {
			const parsedTarget = parseBridgeCallTarget(target);
			if (!isRegistered) return;

			removeTargetFromCurrentUrl();
			onStart({
				aor: parsedTarget,
				displayName: parsedTarget,
			});
		} catch (error) {
			removeTargetFromCurrentUrl();
			pushNotification({
				message: error instanceof Error ? error.message : 'Некорректный адрес вызова.',
				duration: 4000,
				type: 'error',
				hasRemove: true,
			});
		}
	}, [isRegistered, location.hash, location.pathname, location.search]);
};
